import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { salesServiceOrderService } from '../../../services/salesServiceOrderService';
import { useSalesPointOfSale } from '../../../contexts/SalesPointOfSaleContext';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import LazyTableWithMonthGroups from '../../../components/Common/LazyTableWithMonthGroups';
import { useLazyMonthList } from '../../../hooks/useLazyMonthList';
import MutateGate from '../../../components/Common/MutateGate';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import { POS_CONFIRMED_STATE } from '../../../utils/ventasPosNavigation';
import SalesQuoteProgressBar from '../../../components/Ventas/SalesQuoteProgressBar';
import { getSalesQuoteFulfillmentLabel } from '../../../utils/salesQuoteProgress';
import SalesServiceOrderPrintButton from '../../../components/Ventas/SalesServiceOrderPrintButton';
import SalesServiceOrderPdfButton from '../../../components/Ventas/SalesServiceOrderPdfButton';
import ListCurrencyBadge from '../../../components/Common/ListCurrencyBadge';
import { formatMoneyInDocumentCurrency } from '../../../utils/documentCurrency';

const VentasServiceOrdersIndex = () => {
  const canMutate = useCanMutate();
  const { selectedPointOfSale } = useSalesPointOfSale();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [toggleTarget, setToggleTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const buildListParams = useCallback(() => {
    if (!selectedPointOfSale?.id) return {};
    const params = { point_of_sale_id: selectedPointOfSale.id };
    if (statusFilter !== 'all') params.status = statusFilter;
    if (searchTerm.trim()) params.search = searchTerm.trim();
    return params;
  }, [selectedPointOfSale?.id, statusFilter, searchTerm]);

  const fetchSummary = useCallback(
    (params) => {
      if (!params.point_of_sale_id) return Promise.resolve({ success: true, data: { months: [], stats: null } });
      return salesServiceOrderService.getServiceOrders({ ...params, month_summary: 1 });
    },
    []
  );
  const fetchMonth = useCallback((params) => salesServiceOrderService.getServiceOrders(params), []);

  const {
    monthSummaries,
    loadingSummary,
    loadMonthDetail,
    getMonthItems,
    isMonthLoading,
    reloadSummary,
  } = useLazyMonthList({
    buildParams: buildListParams,
    fetchSummary,
    fetchMonth,
    searchTerm,
  });

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const closeToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handlePdfSuccess = useCallback(() => {
    setToast({ isOpen: true, message: 'PDF generado', type: 'success' });
  }, []);

  const handlePdfError = useCallback((message) => {
    setToast({ isOpen: true, message: message || 'Error al generar el PDF', type: 'error' });
  }, []);

  const openActionModal = (order) => {
    closeToast();
    setActionLoading(false);
    setToggleTarget(order);
  };

  const closeActionModal = () => {
    setActionLoading(false);
    setToggleTarget(null);
  };

  const confirmToggle = async () => {
    if (!toggleTarget) return;
    setActionLoading(true);
    try {
      const res = await salesServiceOrderService.toggleStatus(toggleTarget.id);
      setToast({
        isOpen: true,
        message: res.deleted === true ? 'Orden de servicio eliminada' : res.message || 'Operación completada',
        type: 'success',
      });
      await reloadSummary();
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al procesar la acción',
        type: 'error',
      });
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => formatMoneyInDocumentCurrency(amount, currency);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Orden de servicio (venta)</h1>
            <p className="text-gray-600 mt-1">
              Confirma el servicio de una cotización y registra el ingreso en Resultados. El avance se mide
              cuando existe un documento de venta, no antes.
            </p>
          </div>
          <MutateGate>
            <Link
              to="/ventas/service-orders/create"
              state={POS_CONFIRMED_STATE}
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              + Nueva orden de servicio
            </Link>
          </MutateGate>
        </div>

        <form onSubmit={handleSearch} className="mt-6 flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Estado</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="all">Todos</option>
              <option value="issued">Emitidas</option>
              <option value="cancelled">Canceladas</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-600 mb-1">Buscar</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Código, cliente u observación..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-800 text-white rounded-lg">
            Buscar
          </button>
        </form>

        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
          {!selectedPointOfSale?.id ? (
            <p className="p-8 text-center text-gray-500">Selecciona un punto de venta</p>
          ) : loadingSummary && monthSummaries.length === 0 ? (
            <p className="p-8 text-center text-gray-500">Cargando resumen por mes…</p>
          ) : monthSummaries.length === 0 ? (
            <p className="p-8 text-center text-gray-500">No hay órdenes de servicio</p>
          ) : (
            <LazyTableWithMonthGroups
              months={monthSummaries}
              getMonthItems={getMonthItems}
              isMonthLoading={isMonthLoading}
              onMonthExpand={loadMonthDetail}
              defaultCollapsed
              columnCount={12}
              wrapperClassName="min-w-0"
              className="table-fixed"
              emptyMessage="No hay órdenes de servicio"
              headerRow={
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[7%]">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[7%]">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[4%]">Mon.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">Cotización</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[4%]">Ítems</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">Progreso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[10%]">Base imponible</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">IGV</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[9%]">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[8%]">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[14%]">Acciones</th>
                </tr>
              }
              renderRow={(order) => {
                const status = getSalesQuoteFulfillmentLabel(order);
                return (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 font-mono text-sm text-teal-700">
                      <Link to={`/ventas/service-orders/${order.id}/edit`} state={POS_CONFIRMED_STATE}>
                        {order.code}
                      </Link>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">
                      {formatDateLocaleLima(order.issue_date)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <ListCurrencyBadge currency={order.currency} />
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 truncate max-w-[12rem]" title={order.client?.name || ''}>
                      {order.client?.name || '—'}
                    </td>
                    <td className="px-4 py-4 text-sm font-mono text-gray-700">
                      {order.sales_quote?.code || '—'}
                    </td>
                    <td className="px-4 py-4 text-sm">{order.items?.length || 0}</td>
                    <td className="px-4 py-4">
                      <SalesQuoteProgressBar quote={order} className="w-full max-w-[7rem]" />
                    </td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">{formatCurrency(order.base_imponible, order.currency)}</td>
                    <td className="px-4 py-4 text-sm whitespace-nowrap">{formatCurrency(order.igv_amount, order.currency)}</td>
                    <td className="px-4 py-4 text-sm font-medium whitespace-nowrap">{formatCurrency(order.total_amount, order.currency)}</td>
                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        <SalesServiceOrderPrintButton orderId={order.id} />
                        <SalesServiceOrderPdfButton
                          orderId={order.id}
                          onSuccess={handlePdfSuccess}
                          onError={handlePdfError}
                        />
                        <Link
                          to={`/ventas/service-orders/${order.id}/edit`}
                          state={POS_CONFIRMED_STATE}
                          className="text-purple-600 hover:text-purple-900"
                        >
                          {canMutate && order.status !== 'cancelled' ? 'Editar' : 'Ver'}
                        </Link>
                        {order.can_delete !== false ? (
                        <MutateGate>
                          <button
                            type="button"
                            onClick={() => openActionModal(order)}
                            className="text-red-600 hover:text-red-800"
                          >
                            Eliminar
                          </button>
                        </MutateGate>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              }}
            />
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={!!toggleTarget}
        onClose={closeActionModal}
        onConfirm={confirmToggle}
        title="Eliminar orden de servicio"
        message={toggleTarget ? `¿Eliminar la orden "${toggleTarget.code}"? Esta acción no se puede deshacer.` : ''}
        confirmText="Eliminar"
        cancelText="Volver"
        type="danger"
        isLoading={actionLoading}
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={closeToast}
      />
    </div>
  );
};

export default VentasServiceOrdersIndex;
